import { useEffect, useRef, useCallback } from 'react'
import { useBarcodeContext } from '../contexts/BarcodeContext'
import { barcodeEventEmitter } from '../services/BarcodeEventEmitter'
import { BarcodeEvent, BarcodeHandler, PositionData } from '../types/barcode'
import { BarcodeScanner } from '../services/BarcodeScanner'

interface UseBarcodeOptions {
  onPosition?: (position: PositionData) => void
  onError?: (error: Error) => void
  autoStart?: boolean
  filter?: (event: BarcodeEvent) => boolean
}

export const useBarcode = (handler: BarcodeHandler, options: UseBarcodeOptions = {}): void => {
  const { startScanning } = useBarcodeContext()
  const handlerRef = useRef(handler)
  const optionsRef = useRef(options)
  const componentId = useRef(`component-${Math.random().toString(36).substr(2, 9)}`)

  // Update refs when props change
  useEffect(() => {
    handlerRef.current = handler
    optionsRef.current = options
  }, [handler, options])

  const handleBarcodeEvent = useCallback((event: BarcodeEvent) => {
    console.log('🪄 handling barcode event in useBarcode', event)
    try {
      // Apply filter if provided
      if (optionsRef.current.filter && !optionsRef.current.filter(event)) {
        return
      }

      // Handle position events specially
      if (event.type === 'POSITION' && optionsRef.current.onPosition) {
        const positionData = BarcodeScanner.parsePositionCode(event.data)
        if (positionData) {
          optionsRef.current.onPosition(positionData)
        }
      }

      // Call the main handler
      handlerRef.current(event)
    } catch (error) {
      if (optionsRef.current.onError) {
        optionsRef.current.onError(error as Error)
      } else {
        console.error('Error in barcode handler:', error)
      }
    }
  }, [])

  useEffect(() => {
    // Subscribe to global barcode event emitter
    barcodeEventEmitter.on('scan', handleBarcodeEvent)

    // Auto-start if requested
    if (options.autoStart) {
      startScanning()
    }

    // Cleanup subscription
    return () => {
      barcodeEventEmitter.off('scan', handleBarcodeEvent)
    }
  }, [handleBarcodeEvent, startScanning, options.autoStart])
}

// Specialized hook for position scanning
export const useBarcodePosition = (
  handler: (position: PositionData) => void,
  options: Omit<UseBarcodeOptions, 'onPosition'> = {},
): void => {
  useBarcode(
    event => {
      // Only handle position events
      if (event.type === 'POSITION') {
        const positionData = BarcodeScanner.parsePositionCode(event.data)
        if (positionData) {
          handler(positionData)
        }
      }
    },
    {
      ...options,
    },
  )
}

// Hook for product scanning with automatic code/EAN matching
export const useBarcodeProduct = (
  products: Array<{ code?: string; ean?: string }>,
  handler: (matchedProduct: any, event: BarcodeEvent) => void,
  options: UseBarcodeOptions = {},
): void => {
  useBarcode(event => {
    if (event.type !== 'BARCODE') return

    const scannedCode = event.data
    const matchedProduct = products.find(product => product.code === scannedCode || product.ean === scannedCode)

    if (matchedProduct) {
      handler(matchedProduct, event)
    }
  }, options)
}
